import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, RefreshCw, LogOut, Inbox, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KEY = "canopy_admin_pw";

export default function Admin() {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchQuotes = async (password) => {
    setLoading(true);
    try {
      const res = await api.get("/quotes", { headers: { "X-Admin-Password": password } });
      setQuotes(res.data);
      setAuthed(true);
      sessionStorage.setItem(KEY, password);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid password.");
        sessionStorage.removeItem(KEY);
        setAuthed(false);
      } else {
        toast.error("Failed to load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(KEY);
    if (saved) fetchQuotes(saved);
  }, []);

  const login = (e) => {
    e.preventDefault();
    if (!pw) return;
    fetchQuotes(pw);
  };

  const logout = () => {
    sessionStorage.removeItem(KEY);
    setAuthed(false);
    setPw("");
    setQuotes([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6 grain">
        <form onSubmit={login} data-testid="admin-login-form" className="glass-dark p-10 w-full max-w-md">
          <div className="w-14 h-14 border border-gold/40 flex items-center justify-center mb-6">
            <Lock className="text-gold" size={24} />
          </div>
          <h1 className="font-display text-2xl text-cream mb-2">Admin Access</h1>
          <p className="text-cream/50 text-sm mb-8">Canopy Grounds submission dashboard</p>
          <label className="text-xs uppercase tracking-[0.15em] text-cream/60">Password</label>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            data-testid="admin-password-input"
            placeholder="••••••••••"
            className="w-full bg-transparent border-b border-gold/40 focus:border-gold text-cream placeholder-cream/30 py-3 mt-2 mb-8 outline-none transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-gold text-ink py-3 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-gold-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter Dashboard"}
          </button>
          <Link to="/" data-testid="admin-back-home" className="mt-6 flex items-center justify-center gap-2 text-cream/50 hover:text-gold text-sm transition-colors">
            <ArrowLeft size={14} /> Back to website
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-cream grain">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="font-display text-3xl text-cream">Quote Submissions</h1>
            <p className="text-cream/50 text-sm mt-1" data-testid="admin-count">
              {quotes.length} total {quotes.length === 1 ? "request" : "requests"}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchQuotes(sessionStorage.getItem(KEY))}
              data-testid="admin-refresh"
              className="flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 text-sm uppercase tracking-wider hover:bg-gold/10 transition-colors"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 bg-gold text-ink px-5 py-2.5 text-sm uppercase tracking-wider hover:bg-gold-hover transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        {quotes.length === 0 ? (
          <div className="glass-dark p-16 text-center" data-testid="admin-empty">
            <Inbox className="text-gold/60 mx-auto mb-4" size={48} />
            <p className="text-cream/60">No submissions yet.</p>
          </div>
        ) : (
          <div className="glass-dark overflow-x-auto" data-testid="admin-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gold/20 text-gold/80 uppercase text-xs tracking-wider">
                  <th className="p-4">Date</th>
                  <th className="p-4">Name</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Service</th>
                  <th className="p-4">Address</th>
                  <th className="p-4">Start</th>
                  <th className="p-4">Description</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q, i) => (
                  <tr key={q.id} data-testid={`admin-row-${i}`} className="border-b border-cream/10 hover:bg-forest/30 transition-colors align-top">
                    <td className="p-4 text-cream/60 whitespace-nowrap">
                      {new Date(q.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-cream whitespace-nowrap">{q.first_name} {q.last_name}</td>
                    <td className="p-4 text-cream/70">
                      <div>{q.email}</div>
                      <div className="text-cream/50">{q.phone}</div>
                    </td>
                    <td className="p-4"><span className="text-gold">{q.service_type}</span></td>
                    <td className="p-4 text-cream/70 max-w-[180px]">{q.service_address}</td>
                    <td className="p-4 text-cream/60 whitespace-nowrap">{q.preferred_start_date}</td>
                    <td className="p-4 text-cream/60 max-w-[280px]">{q.project_description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
